import { keepPreviousData, type QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { type BoardsListOptions, type Library, SortDirection, type SubnavBoardSort } from '@air/api/types';

import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';
import { useSubnavSort } from '~/swr-hooks/subnav/useSubnavSort';

export const LIBRARY_BOARDS_LIST = 'LIBRARY_BOARDS_LIST';

export type LibraryBoardsListParams = {
  workspaceId?: string;
  sortField?: SubnavBoardSort;
  libraryId: Library['id'] | undefined;
  params?: Partial<BoardsListOptions>;
};

export type LibraryBoardsListKey = [typeof LIBRARY_BOARDS_LIST, LibraryBoardsListParams];

export const getLibraryRootBoardsKey = (params: LibraryBoardsListParams) => {
  return [LIBRARY_BOARDS_LIST, params];
};

export const isLibraryBoardsListKey = (key: QueryKey): key is LibraryBoardsListKey => {
  return Array.isArray(key) && key[0] === LIBRARY_BOARDS_LIST;
};

export const useLibraryRootBoards = ({
  workspaceId,
  libraryId,
  params,
  enabled = true,
}: {
  workspaceId: string | undefined;
  libraryId: Library['id'] | undefined;
  params?: Partial<BoardsListOptions>;
  enabled?: boolean;
}) => {
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();
  const { data: subnavSort } = useSubnavSort({ libraryId });
  const sortField = subnavSort?.libraryBoardSorts?.[0];

  const queryParams: Partial<BoardsListOptions> = {
    isMemberOfBoard: 'ALL',
    limit: 100,
    view: params?.search ? 'all' : 'root',
    includeAncestors: true,
    includeDirectDescendantBoardCount: true,
    numThumbnails: 0,
    sortField: sortField ? { name: sortField.name, direction: sortField.direction } : undefined,
    libraryBoards: 'YES',
    libraryIds: libraryId ? [libraryId] : undefined,
    ...params,
  };

  const infiniteQuery = useInfiniteQuery({
    queryKey: getLibraryRootBoardsKey({
      workspaceId,
      sortField,
      libraryId,
      params: queryParams,
    }),
    queryFn: ({ pageParam }) =>
      privateBoardsFetcher({
        params: queryParams,
        sortFieldName: sortField?.name || 'customSort',
        sortDirection: sortField?.direction || SortDirection.desc,
        cursor: pageParam || null,
      }),

    enabled: !!workspaceId && !!libraryId && enabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
    placeholderData: keepPreviousData,
  });

  return {
    data: infiniteQuery.data?.pages.flatMap((d) => d.data) ?? [],
    total: infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1]?.total ?? 0,
    hasMore: !!infiniteQuery.hasNextPage,
    isLoadingMore: infiniteQuery.isFetchingNextPage,
    isInitialLoading: infiniteQuery.isLoading,
    loadNextPage: infiniteQuery.fetchNextPage,
    refetch: infiniteQuery.refetch,
  };
};
